import { Navigate, useParams } from 'react-router-dom';

import { Artifact } from 'data/generated/graphql';
import { usePublicationSettings } from 'hooks/publication-settings';
import { useIsPublisher } from 'hooks/roles';
import PagesPage from 'pages/PagesPage';
import { ARTIFACT_PRIORITY_ORDER, ARTIFACT_ROUTE_CONFIGS, PUBLISHER_REQUIRED_ARTIFACTS } from 'utils/artifactRoutes';

/**
 * PropertyRedirect component that redirects to the first enabled feature for a property.
 *
 * This component is used on the `/:property` route to intelligently redirect users
 * to the first available feature based on:
 * - Which artifacts are enabled in publicationSettings
 * - The user's role (publisher vs non-publisher)
 * - The priority order defined in ARTIFACT_PRIORITY_ORDER
 *
 * Special case: If Pages is the first enabled artifact, it renders PagesPage directly
 * instead of redirecting (to avoid infinite redirect loop since Pages route is `/:property`).
 *
 * If no artifacts are enabled or accessible, it allows the route to fall through
 * to the default 404 handler.
 */
export const PropertyRedirect = () => {
  const { property } = useParams();
  const { data: publicationSettingsResp, isLoading } = usePublicationSettings();
  const isPublisher = useIsPublisher();

  // Show nothing while loading settings
  if (isLoading) {
    return null;
  }

  const publicationSettings = publicationSettingsResp?.publicationSetting;
  const enabledArtifacts = publicationSettings?.enabledArtifacts ?? [];

  // Find the first enabled artifact that the user has access to
  const firstEnabledArtifact = ARTIFACT_PRIORITY_ORDER.find((artifact) => {
    const isEnabled = enabledArtifacts.includes(artifact);
    const requiresPublisher = PUBLISHER_REQUIRED_ARTIFACTS.includes(artifact);
    const hasAccess = !requiresPublisher || isPublisher;

    return isEnabled && hasAccess;
  });

  // If we found an enabled artifact
  if (firstEnabledArtifact && property) {
    // Special case: Pages artifact routes to /:property (empty path)
    // Render PagesPage directly to avoid redirect loop
    if (firstEnabledArtifact === Artifact.Page) {
      return <PagesPage />;
    }

    // For all other artifacts, redirect to their specific route
    const routeConfig = ARTIFACT_ROUTE_CONFIGS[firstEnabledArtifact];
    const targetPath = `/${property}${routeConfig.path}`;

    return <Navigate to={targetPath} replace />;
  }

  // If no artifacts are enabled or accessible, render nothing
  // This allows React Router to handle the 404 naturally
  return null;
};
